import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '../../../reusable/Button/Button';
import Input from '../../../reusable/Input/Input';
import { useChangePasswordMutation } from '../../../store/api/authApi';
import {
  passwordChangeErrorMessage,
  passwordChangeOutcome,
} from '../../../store/api/authHelpers';
import { toast } from '../../../reusable/Toast/toastStore';
import { PASSWORD_CHANGED_AT } from '../data';
import { t } from '../../../i18n/i18n';
import './Password.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * What the backend accepts, spelled out rather than hidden behind one "invalid
 * password" message — each rule ticks live as it is met, so the trader can see
 * which one is still missing before submitting.
 *
 * This array is the single source: the checklist renders from it and the Yup
 * schema tests against it, so a rule can't be shown and not enforced.
 */
const RULES = [
  { id: 'length', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { id: 'case', label: 'An upper and a lower case letter', test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v) },
  { id: 'number', label: 'At least one number', test: (v) => /\d/.test(v) },
  { id: 'symbol', label: 'At least one symbol', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const passwordSchema = Yup.object({
  current: Yup.string().required('Enter your current password'),
  next: Yup.string()
    .required('Enter a new password')
    // One message for the whole composition: the checklist below already says
    // which rule is outstanding, and four stacked field errors would fight it.
    .test('rules', 'This password does not meet the requirements below', (value = '') =>
      RULES.every((rule) => rule.test(value)),
    )
    .notOneOf(
      [Yup.ref('current')],
      'The new password must be different from your current one',
    ),
  confirm: Yup.string()
    .required('Re-enter the new password')
    .oneOf([Yup.ref('next')], 'Passwords do not match'),
});

const EMPTY = { current: '', next: '', confirm: '' };

/**
 * How the meter reads, indexed by how many of the four rules pass. Tones are
 * the guide's own signal colours — nothing new invented for a strength bar.
 */
const STRENGTH = [
  { label: 'Enter a password', tone: 'empty' },
  { label: 'Weak', tone: 'weak' },
  { label: 'Fair', tone: 'fair' },
  { label: 'Good', tone: 'good' },
  { label: 'Strong', tone: 'strong' },
];

export default function ProfilePassword() {
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const formik = useFormik({
    initialValues: EMPTY,
    validationSchema: passwordSchema,
    onSubmit: async (values, { resetForm, setStatus }) => {
      setStatus(null);

      // Both paths land here — a refusal reads the same whether it came back
      // as a 200 body or as a thrown status.
      const fail = (message) => {
        setStatus({ error: message });
        toast.error(t('Password not changed'), t(message));
      };

      try {
        const data = await changePassword({
          currentPassword: values.current,
          newPassword: values.next,
          confirmPassword: values.confirm,
        }).unwrap();

        // This endpoint answers 200 even when it refuses, so the body decides.
        const outcome = passwordChangeOutcome(data);
        if (!outcome.ok) {
          fail(outcome.message ?? 'We could not change your password.');
          return;
        }

        resetForm();
        setStatus({ success: outcome.message ?? 'Password updated' });
        toast.success(t('Password updated'), t('Use your new password the next time you sign in.'));
      } catch (err) {
        fail(passwordChangeErrorMessage(err));
      }
    },
  });

  /** Only show a field error once the user has left the field or tried to submit. */
  const fieldError = (name) => (formik.touched[name] ? t(formik.errors[name]) : undefined);

  const met = RULES.filter((rule) => rule.test(formik.values.next)).length;
  const strength = STRENGTH[met];

  const field = (name) => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: fieldError(name),
    disabled: isLoading,
  });

  return (
    <section className="profile-section profile-password">
      <div className="profile-head">
        <h2 className="profile-head__title">{t('Password')}</h2>
        <p className="profile-head__desc">
          {t('Change the password you use to sign in to Alpha Capital. This is not your trading platform password — those live under TradeLocker Info and DX Trade Info.')}
        </p>
      </div>

      {/* Whatever the API refused the whole attempt for — wrong current
          password, a rule only the backend knows, the session having expired.
          Field-level problems never reach here. */}
      {formik.status?.error && (
        <p className="pw-banner pw-banner--error" role="alert">
          {t(formik.status.error)}
        </p>
      )}
      {formik.status?.success && (
        <p className="pw-banner pw-banner--success" role="status">
          {t(formik.status.success)}
        </p>
      )}

      {/* The fields take a column of their own and the requirements sit beside
          them, so the guidance is level with the field it describes instead of
          stranded under the form — and Current Password stops leaving half a
          row empty. */}
      <div className="pw-grid">
        <form className="pw-form" onSubmit={formik.handleSubmit} noValidate>
          <Input
            label={t('Current Password')}
            type="password"
            placeholder={t('Enter your current password')}
            autoComplete="current-password"
            {...field('current')}
          />

          <span className="profile-rule" />

          <Input
            label={t('New Password')}
            type="password"
            placeholder={t('Enter a new password')}
            autoComplete="new-password"
            {...field('next')}
          />
          <Input
            label={t('Confirm New Password')}
            type="password"
            placeholder={t('Re-enter the new password')}
            autoComplete="new-password"
            {...field('confirm')}
          />
        </form>

        <aside className={`pw-guide pw-guide--${strength.tone}`}>
          <div className="pw-strength">
            <div className="pw-strength__head">
              <span className="pw-strength__title">{t('Password strength')}</span>
              <span className="pw-strength__label">{t(strength.label)}</span>
            </div>
            <div
              className="pw-strength__track"
              role="meter"
              aria-valuemin={0}
              aria-valuemax={RULES.length}
              aria-valuenow={met}
              aria-label={t('Password strength')}
            >
              {RULES.map((rule, i) => (
                <span
                  key={rule.id}
                  className={`pw-strength__segment${i < met ? ' is-lit' : ''}`}
                />
              ))}
            </div>
          </div>

          <ul className="pw-rules">
            {RULES.map((rule) => {
              const ok = rule.test(formik.values.next);
              return (
                <li key={rule.id} className={`pw-rules__item${ok ? ' is-met' : ''}`}>
                  <span className="pw-rules__mark">
                    {ok && <span className="icon pw-rules__tick" style={icon('check-sm')} />}
                  </span>
                  {t(rule.label)}
                </li>
              );
            })}
          </ul>
        </aside>
      </div>

      <span className="profile-rule" />

      <div className="profile-actions">
        <span className="profile-note profile-password__meta">
          {t('Last changed {{date}}', { date: PASSWORD_CHANGED_AT })}
        </span>
        <Button variant="submit" loading={isLoading} onClick={formik.handleSubmit}>
          {t('Update password')}
        </Button>
      </div>
    </section>
  );
}
